import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, download } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Panel, QuotaBar } from "@/components/Common";
import { LogoUploader } from "@/components/LogoUploader";
import { useBranding, setBrandingLogo } from "@/lib/branding";

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [comps, setComps] = useState([]);
  const { logo } = useBranding();
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    api.get("/admin/companies").then((r) => setComps(r.data));
  }, []);
  if (!s) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const critical = [...comps].sort((a, b) => (b.xml_used / (b.xml_quota || 1)) - (a.xml_used / (a.xml_quota || 1))).slice(0, 8);

  return (
    <div className="space-y-8">
      <PageHeader title="Studio Principale" subtitle="Panoramica della piattaforma, pacchetti e consumi XML">
        <Button variant="outline" className="bg-white" data-testid="dashboard-brochure-button" onClick={() => download("/admin/presentazione.pdf").catch((e) => toast.error(e.message))}><Download className="mr-2 h-4 w-4" />Presentazione PDF</Button>
      </PageHeader>
      {s.requests_new > 0 && (
        <Link to="/admin/richieste" data-testid="new-requests-alert" className="block rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800 transition-colors hover:bg-sky-100">
          {s.requests_new === 1 ? "1 nuova richiesta di account" : `${s.requests_new} nuove richieste di account`} da gestire →
        </Link>
      )}
      <div className="stagger grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Kpi testid="stat-studios" label="Studi" value={s.studios} />
        <Kpi testid="stat-companies" label="Aziende clienti" value={s.companies} />
        <Kpi testid="stat-users" label="Utenti" value={s.users} />
        <Kpi testid="stat-invoices" label="Documenti" value={s.invoices_out + s.invoices_in} hint={`${s.invoices_out} emessi · ${s.invoices_in} ricevuti`} />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Consumo complessivo XML">
          <QuotaBar used={s.xml_used} quota={s.xml_quota} />
          <p className="mt-4 text-xs text-slate-500">{s.packages} pacchetti a listino.</p>
          <div className="mt-6 border-t border-slate-100 pt-5">
            <div className="mb-3 text-sm font-semibold text-slate-800">Logo del sito</div>
            <LogoUploader endpoint="/admin/branding" value={logo} testid="site-logo" hint="Su homepage, login e menu. PNG trasparente consigliato, max 280 KB" onSaved={setBrandingLogo} />
          </div>
        </Panel>
        <Panel title="Aziende con maggior consumo" className="lg:col-span-2" action={<Link to="/admin/aziende" className="text-xs font-semibold text-sky-700">Gestisci</Link>}>
          <ul className="space-y-4" data-testid="usage-list">
            {critical.map((c) => (
              <li key={c.id} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_220px] sm:items-center">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{c.denominazione}</div>
                  <div className="text-xs text-slate-500">{c.package_name || "Nessun pacchetto"} · {c.studio_name || "Nessuno studio"}</div>
                </div>
                <QuotaBar used={c.xml_used} quota={c.xml_quota} />
              </li>
            ))}
            {critical.length === 0 && <li className="text-sm text-slate-500">Nessuna azienda</li>}
          </ul>
        </Panel>
      </div>
      <Panel title="Scarica il pacchetto per il tuo hosting">
        <p className="mb-4 text-sm text-slate-600">Scarica il codice completo (con la guida INSTALL.md) e una copia di tutti i dati. Il file dei dati contiene informazioni riservate: conservalo in un posto sicuro.</p>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="export-code-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => { toast.info("Preparazione del codice…"); download("/admin/export/code").catch((e) => toast.error(e.message)); }}>
            <Download className="mr-2 h-4 w-4" />Scarica codice
          </Button>
          <Button data-testid="export-data-button" variant="outline" onClick={() => { toast.info("Preparazione dei dati…"); download("/admin/export/data").catch((e) => toast.error(e.message)); }}>
            <Download className="mr-2 h-4 w-4" />Scarica dati
          </Button>
        </div>
      </Panel>
    </div>
  );
}
